import {
  Button,
  ButtonProps,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  FormControl,
  FormLabel,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useFetchAccountsQuery, usePutPurchaseOrderMutation } from 'api';
import { DatePicker } from 'chakra-ui-date-input';
import { useAuth } from 'core';
import Decimal from 'decimal.js';
import {
  AppButton,
  AppInput,
  AutoComplete,
  CurrencyLabel,
  DateDisplay,
} from 'features/components';
import { DataTable } from 'features/table';
import { useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { HiPencil } from 'react-icons/hi';
import { PurchaseOrderLineItemResponse } from 'api/bindings/PurchaseOrderLineItemResponse';
import { PutPurchaseOrderLineItemRequest } from 'api/bindings/PutPurchaseOrderLineItemRequest';
import { PutPurchaseOrderRequest } from 'api/bindings/PutPurchaseOrderRequest';

interface Form extends Pick<PutPurchaseOrderRequest, 'id' | 'lineItems'> {
  lineItems: LineItem[];
}

interface LineItem
  // extends Omit<PutPurchaseOrderLineItemRequest, 'subAccountId'> {
  extends PutPurchaseOrderLineItemRequest {
  id: string;
  // subAccountId: string;
  subTotal: string;
  originalSubTotal: string;
  net: string;
}

export interface EditPoLineItemsModalProps {
  purchaseOrderId: string;
  purchaseOrderName?: string;
  lineItems: PurchaseOrderLineItemResponse[];
  buttonProps?: ButtonProps;
}

export const EditPoLineItemsModal = (props: EditPoLineItemsModalProps) => {
  const {
    purchaseOrderId,
    purchaseOrderName = '',
    lineItems: originalLineItems,
    buttonProps,
  } = props;

  const toast = useToast();
  const lineItems = useMemo(
    () => originalLineItems.map(toLineItem),
    [originalLineItems]
  );
  const auth = useAuth();
  const { data: accountsData } = useFetchAccountsQuery(
    { clientId: auth?.user.clientId ?? '0' },
    { skip: !auth?.user.clientId }
  );

  const subAccounts = useMemo(() => {
    if (!accountsData?.items) return [];
    return accountsData.items.flatMap((account) => account.subAccounts);
  }, [accountsData]);
  const [updatePo] = usePutPurchaseOrderMutation();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const form = useForm<Form>({
    mode: 'all',
    defaultValues: { id: purchaseOrderId, lineItems },
  });
  const { handleSubmit, reset, control, watch, formState } = form;

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const onSubmit = async (form: Form) => {
    const validLineItems = form.lineItems.filter(
      (x) => BigInt(x.subAccountId) > 0
    );

    // const filteredLineItems = form.lineItems.filter((x) => x.subAccountId);
    // const putLineItems: PutPurchaseOrderLineItemRequest[] =
    //   filteredLineItems.map((lineItem) => ({
    //     subAccountId: lineItem.subAccountId,
    //     description: lineItem.description || null,
    //     quantityOrdered: lineItem.quantityOrdered,
    //     unitPrice: lineItem.unitPrice,
    //     shipDate: lineItem.shipDate || null,
    //     receivedDate: lineItem.receivedDate || null,
    //     revisedShipDate: lineItem.revisedShipDate || null,
    //     quantityReceived: lineItem.quantityReceived,
    //     quantityBackordered: lineItem.quantityBackordered,
    //     quantityInvoiced: lineItem.quantityInvoiced,
    //     notes: lineItem.notes || null,
    //   }));

    // const request: Form = {
    //   id: purchaseOrderId,
    //   lineItems: putLineItems,
    // };

    // const toDateStr = (s: string | null): string => {
    //   if (s) {
    //     const parts = s.split('-');
    //     const [year, month, day] = parts;

    //     // return new Date(s).toISOString();
    //     return new Date(
    //       parseInt(year),
    //       parseInt(month) - 1,
    //       parseInt(day)
    //     ).toISOString();
    //   }
    //   return undefined as any;
    // };

    validLineItems.forEach((x, ind) => {
      x.id = x.id || (null as any);
      // x.shipDate = toDateStr(x.shipDate);
      // x.receivedDate = toDateStr(x.receivedDate);
      // x.revisedShipDate = toDateStr(x.revisedShipDate);
      validLineItems[ind] = x;
    });

    const request: PutPurchaseOrderRequest = {
      id: purchaseOrderId,
      lineItems: validLineItems,
      assigneeUserId: undefined as any,
      name: undefined as any,
      description: undefined as any,
      commitmentDate: undefined as any,
      reference: undefined as any,
    };

    console.debug(`sending`, request);
    const result = await updatePo(request);
    if ('error' in result) {
      toast({
        title: `Failed to post line items for Purchase Order ${purchaseOrderName}`,
        description: `${(result.error as any)?.data?.message ?? ''}`,
        status: 'error',
      });
      return result;
    }
    toast({
      title: `Line Items for Purchase Order ${purchaseOrderName} posted!`,
      status: 'success',
    });
    handleClose();
    return result;
  };

  const lineItemsWatcher = watch('lineItems');
  const netSum = useMemo(
    () =>
      lineItemsWatcher
        .map((li) => new Decimal(li.net ?? '0'))
        .reduce((sum, n) => sum.add(n), new Decimal(0)),
    [lineItemsWatcher]
  );

  return (
    <>
      <Button
        colorScheme="blue"
        variant="solid"
        leftIcon={
          // @ts-ignore
          <HiPencil />
        }
        {...buttonProps}
        onClick={onOpen}
      >
        Edit Line Items
      </Button>
      <Drawer onClose={onClose} isOpen={isOpen} size="full">
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <DrawerOverlay />
            <DrawerContent>
              <DrawerCloseButton />
              <DrawerHeader>
                Edit Purchase Order Line Items{' '}
                {purchaseOrderName && `for ${purchaseOrderName}`}
              </DrawerHeader>
              <DrawerBody>
                <Stack direction="column" spacing="5" px={{ base: 4, md: 6 }}>
                  <FormControl id="lineItems">
                    <Controller
                      control={control}
                      name="lineItems"
                      rules={{ required: true }}
                      render={({ field }) => (
                        <DataTable
                          value={field.value}
                          onChange={(rows) => {
                            // console.debug('rows onChange', rows);
                            if (rows[rows.length - 1].subAccountId) {
                              rows.push(newLineItem(Number(purchaseOrderId)));
                            }

                            rows = rows.map(balanceLineItem);
                            field.onChange(rows);
                          }}
                          columnFactory={(dcf) => [
                            //   dcf('lineNumber', {
                            //     header: <>#</>,
                            //     render: (c) => (
                            //       <Text textAlign="center">{c && c + 1}</Text>
                            //     ),
                            //   }),
                            dcf('subAccountId', {
                              header: <>Account</>,
                              render: (c) =>
                                subAccounts?.find(
                                  (x: any) => x.subAccountId === c
                                )?.fullSubAccountName,
                              renderEdit: (props) => (
                                <AutoComplete
                                  name=""
                                  items={subAccounts}
                                  valueKey={(v) => v.subAccountId}
                                  textKey={(v) => v.fullSubAccountName}
                                  value={props.value}
                                  onChange={
                                    (v) => props.onValueChange(v as any, true) // TODO: This could be trouble
                                  }
                                  mountErrorField={false}
                                />
                              ),
                            }),
                            dcf('description', {
                              header: <>Description</>,
                              render: (c) => c,
                              renderEdit: (props) => (
                                <AppInput
                                  {...props}
                                  value={props.value || ''}
                                  mountErrorField={false}
                                />
                              ),
                            }),
                            dcf('shipDate', {
                              header: <>Ship Date</>,
                              render: (c) => (
                                <Text textAlign="center">
                                  <DateDisplay value={c} />
                                </Text>
                              ),
                              renderEdit: (props) => (
                                <DatePicker
                                  value={props.value?.toString()}
                                  onChange={(v) => v && props.onValueChange(v)}
                                />
                              ),
                            }),
                            dcf('revisedShipDate', {
                              header: <>Revised Ship Date</>,
                              render: (c) => <DateDisplay value={c} />,
                            }),
                            dcf('originalSubTotal', {
                              header: <>Approved/Pending</>,
                              render: (c) => <CurrencyLabel value={c} />,
                            }),
                            dcf('unitPrice', {
                              header: <>Unit Price</>,
                              render: (c) => <CurrencyLabel value={c || '0'} />,
                              renderEdit: (props) => (
                                <AppInput
                                  mode="currency"
                                  {...props}
                                  mountErrorField={false}
                                  value={props.value || '0'}
                                />
                              ),
                            }),
                            dcf('quantityOrdered', {
                              header: <>Order QTY</>,
                              render: (c) => (
                                <Text textAlign="center">{c}</Text>
                              ),
                              renderEdit: (props) => (
                                <AppInput
                                  mode="number"
                                  {...props}
                                  mountErrorField={false}
                                  value={props.value || '0'}
                                />
                              ),
                            }),
                            dcf('subTotal', {
                              header: <>Ext.</>,
                              render: (c) => <CurrencyLabel value={c} />,
                            }),
                            dcf('net', {
                              header: <>Net</>,
                              render: (c) => <CurrencyLabel value={c} />,
                            }),
                          ]}
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl>
                    <Flex>
                      <FormLabel>Net:</FormLabel>
                      <CurrencyLabel flex={1} value={netSum} />
                    </Flex>
                  </FormControl>
                  <Flex>
                    <Stack direction="row" spacing={4}>
                      <AppButton
                        type="submit"
                        formState={formState}
                        onClick={handleSubmit(onSubmit)}
                      >
                        Submit
                      </AppButton>
                      <AppButton formState={formState} onClick={handleClose}>
                        Cancel
                      </AppButton>
                    </Stack>
                  </Flex>
                </Stack>
              </DrawerBody>
            </DrawerContent>
          </form>
        </FormProvider>
      </Drawer>
    </>
  );
};
function subTotal(input: PurchaseOrderLineItemResponse | LineItem) {
  return new Decimal(input.unitPrice || '0')
    .mul(input.quantityOrdered || '0')
    .toString();
}

function toLineItem(input: PurchaseOrderLineItemResponse): LineItem {
  const s = subTotal(input);

  return {
    id: input.id,
    subAccountId: input.subAccountId,
    description: input.description,
    quantityOrdered: input.quantityOrdered,
    unitPrice: input.unitPrice,
    shipDate: input.shipDate,
    receivedDate: input.receivedDate,
    revisedShipDate: input.revisedShipDate,
    quantityReceived: input.quantityReceived,
    quantityBackordered: input.quantityBackordered,
    quantityInvoiced: input.quantityInvoiced,
    notes: input.notes as any,
    originalSubTotal: s,
    subTotal: s,
    net: '0',
  };
}
function balanceLineItem(input: LineItem): LineItem {
  const s = subTotal(input);
  return {
    ...input,
    subTotal: s,
    net: new Decimal(s).sub(new Decimal(input.originalSubTotal)).toString(),
  };
}

function newLineItem(_purchaseOrderId: number): LineItem {
  return {
    id: '0',
    subAccountId: '0',
    description: null,
    quantityOrdered: '0',
    unitPrice: '0',
    shipDate: null,
    receivedDate: null,
    revisedShipDate: null,
    quantityReceived: '0',
    quantityBackordered: '0',
    quantityInvoiced: '0',
    notes: null as any,
    originalSubTotal: '0',
    subTotal: '0',
    net: '0',
  };
}

// function toTbl(view: PurchaseOrderView): PurchaseOrderTbl {
//   return {
//     id: view.id,
//     afeId: view.afeId,
//     asigneeUserId: view.asigneeUserId,
//     commitmentDate: view.commitmentDate,
//     name: view.name,
//     description: view.description,
//     reference: view.reference,
//     lineItems: view.lineItems,
//     createdById: 0,
//     createdAt: new Date(),
//     updatedById: 0,
//     updatedAt: new Date(),
//   };
// }
